import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { cn } from '../utils/cn';
import { useWallet } from '../contexts/WalletContext';
import {
  Wallet,
  ArrowUpRight,
  ArrowDownLeft,
  Copy,
  Check,
  X,
  QrCode,
  ExternalLink,
  RefreshCw,
  Eye,
  EyeOff,
  TrendingUp,
  TrendingDown,
  Link2,
  Unlink,
  Shield,
} from 'lucide-react';

interface WalletAsset {
  id: string;
  asset: string;
  balance: number;
  available: number;
  locked: number;
  depositAddress?: string;
  usdValue?: number;
  change24h?: number;
}

const ASSET_INFO: Record<string, { name: string; icon: string; color: string }> = {
  BTC: { name: 'Bitcoin', icon: '₿', color: '#f7931a' },
  ETH: { name: 'Ethereum', icon: 'Ξ', color: '#627eea' },
  USDT: { name: 'Tether', icon: '₮', color: '#26a17b' },
  SOL: { name: 'Solana', icon: '◎', color: '#9945ff' },
  BNB: { name: 'BNB', icon: 'B', color: '#f0b90b' },
};

export default function Wallets() {
  const queryClient = useQueryClient();
  const { tradingMode, wallet: connectedWallet, connect, disconnect } = useWallet();
  const [showBalances, setShowBalances] = useState(true);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<WalletAsset | null>(null);
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState<'dex' | 'cex'>(tradingMode === 'WALLET' ? 'dex' : 'cex');

  // Update view mode when trading mode changes
  useEffect(() => {
    setViewMode(tradingMode === 'WALLET' ? 'dex' : 'cex');
  }, [tradingMode]);

  // Fetch CEX/custodial balances
  const { data: wallets = [], isLoading } = useQuery<WalletAsset[]>({
    queryKey: ['wallets'],
    queryFn: () => api.get('/trading/wallets').then((r) => r.data),
    enabled: viewMode === 'cex',
  });

  // Fetch connected wallet balances (DEX mode)
  const { data: dexBalances = [], isLoading: isDexLoading, refetch: refetchDexBalances } = useQuery<WalletAsset[]>({
    queryKey: ['dex-balances', connectedWallet.address, connectedWallet.chainId],
    queryFn: async () => {
      if (!connectedWallet.isConnected || !connectedWallet.address) {
        return [];
      }
      try {
        // Try to get balances from our backend which aggregates data
        const res = await api.get(`/v2/trading/wallet/balances?address=${connectedWallet.address}&chainId=${connectedWallet.chainId}`);
        return res.data.balances || [];
      } catch {
        // Fallback: Return mock data showing connected wallet
        return [
          { id: 'eth', asset: 'ETH', balance: 0, available: 0, locked: 0, usdValue: 0 },
          { id: 'usdt', asset: 'USDT', balance: 0, available: 0, locked: 0, usdValue: 0 },
          { id: 'usdc', asset: 'USDC', balance: 0, available: 0, locked: 0, usdValue: 0 },
        ];
      }
    },
    enabled: viewMode === 'dex' && connectedWallet.isConnected,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Use appropriate balances based on view mode
  const displayWallets = viewMode === 'dex' ? dexBalances : wallets;
  const isLoadingAny = viewMode === 'dex' ? isDexLoading : isLoading;

  const depositMutation = useMutation({
    mutationFn: (asset: string) => api.post(`/trading/wallets/${asset}/deposit`),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      toast.success('Deposit address generated');
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: (data: { asset: string; amount: number; address: string }) =>
      api.post(`/trading/wallets/${data.asset}/withdraw`, { amount: data.amount, address: data.address }),
    onSuccess: () => {
      toast.success('Withdrawal request submitted');
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      closeWithdrawModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Withdrawal failed');
    },
  });

  const totalBalance = displayWallets.reduce((sum, w) => sum + (w.usdValue || w.balance * 50000), 0);
  
  const handleDeposit = (asset: WalletAsset) => {
    setSelectedAsset(asset);
    setShowDepositModal(true);
    if (!asset.depositAddress) {
      depositMutation.mutate(asset.asset);
    }
  };

  const handleWithdraw = (asset: WalletAsset) => {
    setSelectedAsset(asset);
    setShowWithdrawModal(true);
  };

  const closeDepositModal = () => {
    setShowDepositModal(false);
    setSelectedAsset(null);
  };

  const closeWithdrawModal = () => {
    setShowWithdrawModal(false);
    setSelectedAsset(null);
    setWithdrawAddress('');
    setWithdrawAmount('');
  };

  const copyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Address copied');
  };

  const submitWithdraw = () => {
    if (!selectedAsset || !withdrawAddress || !withdrawAmount) {
      toast.error('Please fill in all fields');
      return;
    }
    withdrawMutation.mutate({
      asset: selectedAsset.asset,
      amount: parseFloat(withdrawAmount),
      address: withdrawAddress,
    });
  };

  const formatBalance = (value: number) => {
    if (!showBalances) return '••••••';
    return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
  };

  const formatUSD = (value: number) => {
    if (!showBalances) return '$••••••';
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Wallet</h1>
          <p className="text-[#848e9c]">Manage your crypto assets</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Mode Toggle */}
          <div className="flex items-center bg-[#1e2329] rounded-xl p-1">
            <button
              onClick={() => setViewMode('dex')}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                viewMode === 'dex'
                  ? "bg-[#00d4aa] text-black"
                  : "text-[#848e9c] hover:text-white"
              )}
            >
              <Link2 className="h-4 w-4 inline-block mr-2" />
              Connected Wallet
            </button>
            <button
              onClick={() => setViewMode('cex')}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                viewMode === 'cex'
                  ? "bg-[#00d4aa] text-black"
                  : "text-[#848e9c] hover:text-white"
              )}
            >
              <Shield className="h-4 w-4 inline-block mr-2" />
              Exchange Balance
            </button>
          </div>
          <button
            onClick={() => setShowBalances(!showBalances)}
            className="btn-ghost btn-md"
          >
            {showBalances ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
          </button>
          <button
            onClick={() => {
              if (viewMode === 'dex') {
                refetchDexBalances();
              } else {
                queryClient.invalidateQueries({ queryKey: ['wallets'] });
              }
            }}
            className="btn-secondary btn-md"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* DEX Mode - Wallet Connection Card */}
      {viewMode === 'dex' && (
        <div className="card mb-6 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={cn(
                "w-12 h-12 rounded-xl flex items-center justify-center",
                connectedWallet.isConnected ? "bg-[#00d4aa]/20" : "bg-[#1e2329]"
              )}>
                {connectedWallet.isConnected ? (
                  <Link2 className="h-6 w-6 text-[#00d4aa]" />
                ) : (
                  <Unlink className="h-6 w-6 text-[#848e9c]" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-white">
                  {connectedWallet.isConnected ? 'Wallet Connected' : 'No Wallet Connected'}
                </h3>
                {connectedWallet.isConnected ? (
                  <p className="text-sm text-[#00d4aa] font-mono">
                    {connectedWallet.address?.slice(0, 6)}...{connectedWallet.address?.slice(-4)}
                  </p>
                ) : (
                  <p className="text-sm text-[#848e9c]">Connect your wallet to view DEX balances</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {connectedWallet.isConnected ? (
                <>
                  <a
                    href={`https://etherscan.io/address/${connectedWallet.address}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-ghost btn-sm"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View on Explorer
                  </a>
                  <button
                    onClick={disconnect}
                    className="btn-secondary btn-sm text-[#ef4444] border-[#ef4444]/30 hover:bg-[#ef4444]/10"
                  >
                    <Unlink className="h-4 w-4" />
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={connect}
                  disabled={connectedWallet.isConnecting}
                  className="btn-primary btn-md"
                >
                  {connectedWallet.isConnecting ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Link2 className="h-4 w-4" />
                      Connect Wallet
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Portfolio Overview */}
      <div className="card mb-8 overflow-hidden">
        <div className="bg-gradient-to-r from-[#1e2329] to-[#2b3139] p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-[#848e9c] mb-2">Estimated Balance</div>
              <div className="flex items-baseline gap-4">
                <span className="text-4xl font-bold text-white">{formatUSD(totalBalance)}</span>
                <span className="flex items-center gap-1 text-[#0ecb81] text-sm font-medium">
                  <TrendingUp className="h-4 w-4" />
                  +5.23%
                </span>
              </div>
              <div className="mt-2 text-[#848e9c] text-sm">
                ≈ {formatBalance(totalBalance / 98000)} BTC
              </div>
            </div>
            <div className="flex gap-3">
              {viewMode === 'cex' ? (
                <>
                  <Link to="/deposit" className="btn-primary btn-lg">
                    <ArrowDownLeft className="h-5 w-5" />
                    Deposit
                  </Link>
                  <Link to="/withdraw" className="btn-secondary btn-lg">
                    <ArrowUpRight className="h-5 w-5" />
                    Withdraw
                  </Link>
                </>
              ) : (
                <a 
                  href={connectedWallet.isConnected ? `https://app.uniswap.org/#/swap` : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    "btn-primary btn-lg",
                    !connectedWallet.isConnected && "opacity-50 cursor-not-allowed"
                  )}
                  onClick={(e) => {
                    if (!connectedWallet.isConnected) {
                      e.preventDefault();
                      toast.error('Please connect your wallet first');
                    }
                  }}
                >
                  <ExternalLink className="h-5 w-5" />
                  Swap on DEX
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assets */}
      <div className="card">
        <div className="px-6 py-4 border-b border-white/[0.06]">
          <h2 className="text-lg font-semibold text-white">Your Assets</h2>
        </div>
        
        {isLoadingAny ? (
          <div className="p-12 text-center">
            <div className="spinner mx-auto mb-4" />
            <p className="text-[#848e9c]">Loading assets...</p>
          </div>
        ) : viewMode === 'dex' && !connectedWallet.isConnected ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#1e2329] flex items-center justify-center mx-auto mb-4">
              <Unlink className="h-8 w-8 text-[#848e9c]" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Wallet Not Connected</h3>
            <p className="text-[#848e9c] mb-6">Connect your wallet to view your on-chain balances</p>
            <button onClick={connect} disabled={connectedWallet.isConnecting} className="btn-primary btn-md">
              <Link2 className="h-5 w-5" />
              {connectedWallet.isConnecting ? 'Connecting...' : 'Connect Wallet'}
            </button>
          </div>
        ) : displayWallets.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#1e2329] flex items-center justify-center mx-auto mb-4">
              <Wallet className="h-8 w-8 text-[#848e9c]" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">No assets yet</h3>
            <p className="text-[#848e9c] mb-6">
              {viewMode === 'cex' ? 'Deposit crypto to start trading' : 'Your connected wallet has no tokens'}
            </p>
            {viewMode === 'cex' && (
              <Link to="/deposit" className="btn-primary btn-md">
                <ArrowDownLeft className="h-5 w-5" />
                Make Your First Deposit
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-modern">
              <thead>
                <tr>
                  <th>Asset</th>
                  <th className="text-right">Total Balance</th>
                  <th className="text-right">Available</th>
                  <th className="text-right">In Orders</th>
                  <th className="text-right">USD Value</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayWallets.map((assetWallet) => {
                  const info = ASSET_INFO[assetWallet.asset] || { name: assetWallet.asset, icon: assetWallet.asset[0], color: '#848e9c' };
                  return (
                    <tr key={assetWallet.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg"
                            style={{ backgroundColor: info.color + '20', color: info.color }}
                          >
                            {info.icon}
                          </div>
                          <div>
                            <div className="font-medium text-white">{assetWallet.asset}</div>
                            <div className="text-xs text-[#848e9c]">{info.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-right font-mono text-white">{formatBalance(assetWallet.balance)}</td>
                      <td className="text-right font-mono text-[#0ecb81]">{formatBalance(assetWallet.available)}</td>
                      <td className="text-right font-mono text-[#848e9c]">{formatBalance(assetWallet.locked)}</td>
                      <td className="text-right">
                        <div className="font-medium text-white">{formatUSD(assetWallet.usdValue || assetWallet.balance * 50000)}</div>
                        {assetWallet.change24h && (
                          <div className={cn(
                            "text-xs flex items-center justify-end gap-1",
                            assetWallet.change24h >= 0 ? "text-[#0ecb81]" : "text-[#f6465d]"
                          )}>
                            {assetWallet.change24h >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                            {Math.abs(assetWallet.change24h).toFixed(2)}%
                          </div>
                        )}
                      </td>
                      <td className="text-right">
                        {viewMode === 'cex' ? (
                          <div className="flex items-center justify-end gap-2">
                            <Link
                              to="/deposit"
                              className="btn-ghost btn-xs text-[#0ecb81] hover:text-[#0ecb81] hover:bg-[#0ecb81]/10"
                            >
                              Deposit
                            </Link>
                            <Link
                              to="/withdraw"
                              className="btn-ghost btn-xs"
                            >
                              Withdraw
                            </Link>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <a
                              href={`https://app.uniswap.org/#/swap?outputCurrency=${assetWallet.asset === 'ETH' ? 'ETH' : ''}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-ghost btn-xs text-[#0ecb81] hover:text-[#0ecb81] hover:bg-[#0ecb81]/10"
                            >
                              Swap
                            </a>
                            <a
                              href={`https://etherscan.io/address/${connectedWallet.address}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-ghost btn-xs"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Deposit Modal */}
      {showDepositModal && selectedAsset && (
        <div className="modal-overlay" onClick={closeDepositModal}>
          <div className="modal-content w-full max-w-md p-0" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
              <h2 className="text-xl font-bold text-white">Deposit {selectedAsset.asset}</h2>
              <button onClick={closeDepositModal} className="btn-ghost p-2">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-20 h-20 rounded-2xl bg-[#2b3139] flex items-center justify-center mx-auto mb-4">
                  <QrCode className="h-10 w-10 text-[#f0b90b]" />
                </div>
                <p className="text-[#848e9c] text-sm">
                  Send only {selectedAsset.asset} to this address. Sending any other asset may result in permanent loss.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#848e9c] mb-2">Network</label>
                  <select className="select">
                    <option>{selectedAsset.asset} Network</option>
                    <option>ERC20</option>
                    <option>BEP20</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#848e9c] mb-2">Deposit Address</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={selectedAsset.depositAddress || 'Generating...'}
                      readOnly
                      className="input pr-12 font-mono text-sm"
                    />
                    <button
                      onClick={() => selectedAsset.depositAddress && copyAddress(selectedAsset.depositAddress)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#848e9c] hover:text-white"
                    >
                      {copied ? <Check className="h-5 w-5 text-[#0ecb81]" /> : <Copy className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="bg-[#f0b90b]/10 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#f0b90b]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#f0b90b] text-sm font-bold">!</span>
                    </div>
                    <div className="text-sm text-[#f0b90b]">
                      <p className="font-medium mb-1">Important</p>
                      <ul className="list-disc list-inside space-y-1 text-[#f0b90b]/80">
                        <li>Minimum deposit: 0.0001 {selectedAsset.asset}</li>
                        <li>Confirmations required: 3</li>
                        <li>Expected arrival: ~30 minutes</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-white/[0.06] bg-[#12161c]">
              <button onClick={closeDepositModal} className="btn-secondary btn-md w-full">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal && selectedAsset && (
        <div className="modal-overlay" onClick={closeWithdrawModal}>
          <div className="modal-content w-full max-w-md p-0" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
              <h2 className="text-xl font-bold text-white">Withdraw {selectedAsset.asset}</h2>
              <button onClick={closeWithdrawModal} className="btn-ghost p-2">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-[#848e9c]">Available</label>
                  <span className="text-sm text-white font-mono">{selectedAsset.available} {selectedAsset.asset}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#848e9c] mb-2">Recipient Address</label>
                <input
                  type="text"
                  value={withdrawAddress}
                  onChange={(e) => setWithdrawAddress(e.target.value)}
                  className="input font-mono text-sm"
                  placeholder="Enter wallet address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#848e9c] mb-2">Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    className="input pr-24 font-mono"
                    placeholder="0.00"
                  />
                  <button
                    onClick={() => setWithdrawAmount(selectedAsset.available.toString())}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-[#f0b90b] hover:text-[#f8d12f]"
                  >
                    MAX
                  </button>
                </div>
              </div>

              <div className="bg-[#1e2329] rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#848e9c]">Network Fee</span>
                  <span className="text-white">0.0005 {selectedAsset.asset}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#848e9c]">You will receive</span>
                  <span className="text-white font-medium">
                    {withdrawAmount ? (parseFloat(withdrawAmount) - 0.0005).toFixed(8) : '0'} {selectedAsset.asset}
                  </span>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-white/[0.06] bg-[#12161c] flex gap-3">
              <button onClick={closeWithdrawModal} className="btn-secondary btn-md flex-1">
                Cancel
              </button>
              <button
                onClick={submitWithdraw}
                disabled={withdrawMutation.isPending || !withdrawAddress || !withdrawAmount}
                className="btn-primary btn-md flex-1"
              >
                {withdrawMutation.isPending ? 'Processing...' : 'Withdraw'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
